/**
 * 棋盘状态：玩家标记网格 + 行/列/区的星数增量计数。
 *
 * 计数用增量维护而非每次全盘扫描 —— 通关判定在每次落子后都要跑，
 * dim=11 时全扫是 121 格 ×（行+列+区），增量维护则是 O(1)。
 */

import { EMPTY, countsAsStar } from './cellState.js';

export class BoardState {
    /**
     * @param {number} dim
     * @param {number[][]} regionGrid 区域 id 网格，id 从 1 起
     * @param {number} stars 每行/列/区应有的星数
     */
    constructor(dim, regionGrid, stars) {
        this.dim = dim;
        this.regionGrid = regionGrid;
        this.stars = stars;

        /** @type {number[][]} 玩家标记 */
        this.grid = Array.from({ length: dim }, () => new Array(dim).fill(EMPTY));

        this.regionCount = 0;
        for (const row of regionGrid)
            for (const g of row) if (g > this.regionCount) this.regionCount = g;

        this.starsInRow = new Int32Array(dim);
        this.starsInCol = new Int32Array(dim);
        /** 索引 0 不用，区域 id 从 1 起。 */
        this.starsInRegion = new Int32Array(this.regionCount + 1);
        this.totalStars = 0;
    }

    /** @param {number} r @param {number} c */
    get(r, c) {
        return this.grid[r][c];
    }

    /** @param {number} r @param {number} c */
    regionAt(r, c) {
        return this.regionGrid[r][c];
    }

    /** @param {number} r @param {number} c */
    inBounds(r, c) {
        return r >= 0 && r < this.dim && c >= 0 && c < this.dim;
    }

    /**
     * 落子并维护计数。
     * @param {number} r @param {number} c @param {number} next 新状态
     * @returns {number} 旧状态
     */
    set(r, c, next) {
        const prev = this.grid[r][c];
        if (prev === next) return prev;

        const wasStar = countsAsStar(prev);
        const isStar = countsAsStar(next);
        this.grid[r][c] = next;

        if (wasStar !== isStar) {
            const d = isStar ? 1 : -1;
            this.starsInRow[r] += d;
            this.starsInCol[c] += d;
            this.starsInRegion[this.regionGrid[r][c]] += d;
            this.totalStars += d;
        }
        return prev;
    }

    /**
     * 是否有两颗星八向相邻。用于规则校验与错误高亮。
     * @returns {boolean}
     */
    hasAdjacentStars() {
        for (let r = 0; r < this.dim; r++)
            for (let c = 0; c < this.dim; c++) {
                if (!countsAsStar(this.grid[r][c])) continue;
                for (let dr = -1; dr <= 1; dr++)
                    for (let dc = -1; dc <= 1; dc++) {
                        if (dr === 0 && dc === 0) continue;
                        const nr = r + dr, nc = c + dc;
                        if (!this.inBounds(nr, nc)) continue;
                        if (countsAsStar(this.grid[nr][nc])) return true;
                    }
            }
        return false;
    }

    /**
     * 是否已解开：每行/每列/每区星数都等于 stars，且无相邻。
     *
     * 注意：StarDoku 的落星必须命中唯一解才会变成 STAR，所以正常玩法下
     * 星数达标就已保证正确；相邻检查是为了兼容将来可能放宽的模式。
     * @returns {boolean}
     */
    isComplete() {
        if (this.totalStars !== this.stars * this.dim) return false;
        for (let i = 0; i < this.dim; i++) {
            if (this.starsInRow[i] !== this.stars) return false;
            if (this.starsInCol[i] !== this.stars) return false;
        }
        for (let g = 1; g <= this.regionCount; g++)
            if (this.starsInRegion[g] !== this.stars) return false;
        return !this.hasAdjacentStars();
    }

    /** 清空所有标记（重试本关）。 */
    clear() {
        for (let r = 0; r < this.dim; r++) this.grid[r].fill(EMPTY);
        this.starsInRow.fill(0);
        this.starsInCol.fill(0);
        this.starsInRegion.fill(0);
        this.totalStars = 0;
    }

    /** 快照，用于存档。 */
    toArray() {
        return this.grid.map(row => row.slice());
    }
}
